/**
 * Local edit-buffer for a settings screen.
 *
 * A settings page loads server state via a query hook, then edits a *draft*
 * copy. This hook owns that draft:
 *  - seeds it from the loaded value;
 *  - re-seeds whenever a new server value arrives (e.g. after a successful save,
 *    which writes the fresh `version` into the cache — see `useSettingsMutation`);
 *  - tracks whether the draft diverges from the loaded baseline (`dirty`);
 *  - exposes granular `setField` / `patch` setters and a `reset`.
 *
 * Dirty-tracking uses structural equality on a stable JSON projection, which is
 * sufficient for the plain, serialisable settings shapes in this feature.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface SettingsForm<T> {
  /** The editable draft, or `null` until the first server value arrives. */
  draft: T | null;
  /** Update a single field. */
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Shallow-merge a partial update into the draft. */
  patch: (partial: Partial<T>) => void;
  /** Replace the whole draft. */
  setDraft: (value: T) => void;
  /** Discard local edits, restoring the last loaded baseline. */
  reset: () => void;
  /** True when the draft differs from the loaded baseline. */
  dirty: boolean;
  /** True once a draft exists (server value has loaded at least once). */
  ready: boolean;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return Object.keys(val as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = (val as Record<string, unknown>)[key];
          return acc;
        }, {});
    }
    return val;
  });
}

export function useSettingsForm<T>(loaded: T | undefined): SettingsForm<T> {
  const [draft, setDraftState] = useState<T | null>(loaded ?? null);
  const baselineRef = useRef<T | null>(loaded ?? null);

  // Re-seed the draft whenever a new server value arrives. TanStack Query keeps
  // a stable reference for unchanged data (structural sharing), so this only
  // fires on a genuine change — it never clobbers in-progress edits on a no-op
  // background refetch.
  useEffect(() => {
    if (loaded !== undefined) {
      baselineRef.current = loaded;
      setDraftState(loaded);
    }
  }, [loaded]);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setDraftState((current) => (current ? { ...current, [key]: value } : current));
  }, []);

  const patch = useCallback((partial: Partial<T>) => {
    setDraftState((current) => (current ? { ...current, ...partial } : current));
  }, []);

  const setDraft = useCallback((value: T) => {
    setDraftState(value);
  }, []);

  const reset = useCallback(() => {
    setDraftState(baselineRef.current);
  }, []);

  const dirty = useMemo(() => {
    if (draft === null || baselineRef.current === null) {
      return false;
    }
    return stableStringify(draft) !== stableStringify(baselineRef.current);
  }, [draft]);

  return { draft, setField, patch, setDraft, reset, dirty, ready: draft !== null };
}
